import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchReadyForDispatch } from "slices/Warehouse/Dispatch/ReadyForDispatch/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
type ReadyForDispatchProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const ReadyForDispatch: React.FC<ReadyForDispatchProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Ready For Dispatch";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selectors
  const selectReadyForDispatch = createSelector(
    (state: any) => state.ReadyForDispatch,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectReadyForDispatch);

  // Use custom hook to fetch and access permissions
  const { canCreate, canView, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.dispatch
  );

  // Fetch ReadyForDispatch list
  useEffect(() => {
    dispatch(
      fetchReadyForDispatch({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  const handleRevertRequestDispatch = useCallback(
    (row: any) => {
      // Show confirmation popup first
      showCustomSwal({
        title: "Are you sure?",
        text: "Do you want to revert this request for dispatch?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, revert it!",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          let status = "pending dispatch";

          const hasCancelled = row?.order_items.some(
            (item: any) =>
              item?.order_item_status?.toLowerCase() === "cancelled"
          );

          if (hasCancelled) {
            status = "partially cancelled";
          }

          // Show loading modal
          showCustomSwal({
            title: "Reverting...",
            text: "Please wait while we process your request.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          Axios.patch(`${api?.order?.request_dispatch}/${row?.order_id}`, {
            status,
          })
            .then((result) => {
              // Replace loading modal with success modal
              showCustomSwal({
                icon: "success",
                title: "Reverted Successfully",
                text: "Request for dispatch reverted successfully.",
              });

              // Refresh data with current warehouse value
              dispatch(
                fetchReadyForDispatch({
                  page: pageIndex + 1,
                  pageSize,
                  sort: sortField,
                  order: sortField ? (sortDirection as "asc" | "desc") : "",
                  search,
                  filters: filterValue,
                  warehouse_id: warehouseIdSearch || "",
                })
              );
            })
            .catch((err) => {
              // Replace loading modal with error modal
              showCustomSwal({
                icon: "error",
                title: "Failed to revert",
                text:
                  err?.response?.data?.message ||
                  "Failed to revert request for dispatch.",
              });
              console.error("Revert request error:", err);
            });
        }
      });
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
      warehouseIdSearch,
    ]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: AddCircleOutlineIcon,
        label: "Create Dispatch",
        permission: (row: any) => {
          return !!(
            canCreate && row?.order_status?.toLowerCase() !== "dispatched"
          );
        },
        onClick: (ord: any) => {
          navigate(
            `/warehouse-dispatch/ready-for-dispatch/create-dispatch/${ord?.order_id}`
          );
        },
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "View Order Details",
        permission: canView,
        onClick: (ord: any) => {
          navigate(
            `/warehouse-dispatch/ready-for-dispatch/view-details/${ord?.order_id}`
          );
        },
        color: "themeColor",
      },
      {
        icon: UndoIcon,
        label: "Revert to Order",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.order_status?.toLowerCase() === "requested for dispatch"
          );
        },
        onClick: (row: any) => {
          handleRevertRequestDispatch(row);
        },
        color: "error",
      },
    ];
    return items;
  }, [navigate, handleRevertRequestDispatch]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate;

    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },

      {
        header: "Order No.",
        accessorKey: "order_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/ready-for-dispatch/dropdown/order_no/?dispatch_from_warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.order_no || "-",
      },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/ready-for-dispatch/dropdown/quotation_number/?dispatch_from_warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original?.quotation?.quotation_number || "-",
      },

      {
        header: "Order Date",
        accessorKey: "order_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        filterFieldFrom: "order_date_from",
        filterFieldTo: "order_date_to",
        cell: ({ row }: { row: any }) => {
          const date = row.original.order_date
            ? new Date(row.original.order_date).toISOString().split("T")[0]
            : "-";
          return date;
        },
      },
      {
        header: "Order Status",
        accessorKey: "order_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/ready-for-dispatch/dropdown/order_status/?dispatch_from_warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return (
            <OrderStatusBadge status={row?.original?.order_status || "-"} />
          );
        },
      },

      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/ready-for-dispatch/dropdown/customer_name/?dispatch_from_warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original.customer_details?.customer_name || "-",
      },

      {
        header: "Dispatch From Warehouse",
        accessorKey: "warehouse_name",
        enableSorting: false,
        enableColumnFilter: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/dispatch/ready-for-dispatch/dropdown/warehouse_name/?dispatch_from_warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          row.original.warehouse?.warehouse_name || "-",
      },
      {
        header: "Created By / Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By / Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        // filterFetchUrl:`/inventory/dispatch/issue-to-labour/dropdown/created_at/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },

      ...DynamicAction,
    ],
    [dispatch, list, canUpdate, canView, pageIndex, pageSize, warehouseIdSearch]
  );

  return (
    <Row>
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={list}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ReadyForDispatch;
